import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { http } from "api/http";
import {
  bulkImportNonRecurringTasks,
  downloadNonRecurringJobErrors,
  getNonRecurringBulkImportJobs,
  getNonRecurringJobErrors,
} from "api/services/tasks/tasks";
import {
  bulkImportRecurringTasks,
  downloadJobErrors as downloadRecurringJobErrors,
  getBulkImportJobs as getRecurringBulkImportJobs,
  getJobErrors as getRecurringJobErrors,
} from "api/services/recurring";
import DrawerWrapper from "components/DrawerWrapper";
import FileDrop from "components/FileDrop";
import LoadingButton from "components/LoadingButton";
import { snack } from "components/toast";
import { FILETYPES } from "data/constants";
import useTitle from "hooks/useTitle";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { handleError } from "utils/handleError";

const PRIMARY = "#182F53";
const SECONDARY = "#E44652";
const POLL_INTERVAL = 3000;
const MAX_CONSECUTIVE_ERRORS = 10;

// ─── Shared polling hook ──────────────────────────────────────────────────────

function useJobPolling(
  jobId: number | null,
  pollUrl: (id: number) => string,
  onComplete: () => void
) {
  const [job, setJob] = useState<any>(null);
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const errorCountRef = useRef(0);
  const prevStatusRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setIsPolling(false);
  }, []);

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await http.get(pollUrl(jobId));
      const data: any = res.data;
      errorCountRef.current = 0;
      setJob(data);
      if (data.status === "completed" || data.status === "failed") {
        stopPolling();
        if (prevStatusRef.current === "processing") {
          if (data.status === "completed") snack.success("Import completed!");
          else snack.error("Import failed. Check errors below.");
          onComplete();
        }
        prevStatusRef.current = data.status;
      }
    } catch {
      errorCountRef.current += 1;
      // if (errorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
      //   stopPolling();
      //   snack.error("Lost connection to server. Refresh to check import status.");
      // }
    }
  }, [jobId, pollUrl, stopPolling, onComplete]);

  const loadJob = useCallback(async () => {
    if (!jobId) return;
    stopPolling();
    errorCountRef.current = 0;
    try {
      const res = await http.get(pollUrl(jobId));
      const data: any = res.data;
      setJob(data);
      prevStatusRef.current = data.status;
      if (data.status === "processing") {
        setIsPolling(true);
        intervalRef.current = setInterval(fetchJob, POLL_INTERVAL);
      }
    } catch {
      snack.error("Failed to load import job.");
    }
  }, [jobId, fetchJob, pollUrl, stopPolling]);

  useEffect(() => { if (jobId) loadJob(); }, [jobId]);
  useEffect(() => () => stopPolling(), [stopPolling]);

  return { job, isPolling, setJob };
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function GradientProgressBar({ value, indeterminate, failed }: { value: number; indeterminate?: boolean; failed?: boolean }) {
  const barColor = failed ? SECONDARY : `linear-gradient(90deg, ${PRIMARY} 0%, #2d6a9f 50%, ${SECONDARY} 100%)`;
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.8}>
        <Typography variant="caption" color="text.secondary">Progress</Typography>
        {!indeterminate && (
          <Typography variant="caption" sx={{ fontFamily: "muli_bold", color: failed ? SECONDARY : PRIMARY }}>{value}%</Typography>
        )}
      </Box>
      <Box sx={{ position: "relative", height: 10, borderRadius: 5, bgcolor: "rgba(24,47,83,0.08)", overflow: "hidden" }}>
        {indeterminate ? (
          <Box sx={{
            position: "absolute", top: 0, height: "100%", width: "40%",
            background: `linear-gradient(90deg, ${PRIMARY} 0%, ${SECONDARY} 100%)`,
            borderRadius: 5, animation: "slide 1.4s infinite ease-in-out",
            "@keyframes slide": { "0%": { left: "-40%" }, "100%": { left: "100%" } },
          }} />
        ) : (
          <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${value}%`, background: barColor, borderRadius: 5, transition: "width 0.6s ease" }} />
        )}
      </Box>
    </Box>
  );
}

function StatCard({ label, value, color, bgcolor }: { label: string; value: number | undefined; color: string; bgcolor: string }) {
  return (
    <Box flex={1} sx={{ minWidth: 70, bgcolor, borderRadius: 2, px: 1.5, py: 1.2, textAlign: "center", border: `1px solid ${color}22` }}>
      <Typography sx={{ fontFamily: "muli_bold", fontSize: 20, color, lineHeight: 1.2 }}>{value ?? 0}</Typography>
      <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.3, display: "block", fontSize: 10 }}>{label}</Typography>
    </Box>
  );
}

function statusColor(status: string): "success" | "error" | "warning" {
  if (status === "completed") return "success";
  if (status === "failed") return "error";
  return "warning";
}

function getPhaseText(job: any): string {
  if (!job) return "";
  if (job.status === "failed") return "Import failed — see errors below.";
  if (job.status === "completed") {
    if (job.successCount === 0) return "All rows failed validation.";
    if (job.errorCount === 0) return `All ${job.successCount} task(s) created successfully.`;
    return `Completed — ${job.successCount} created, ${job.errorCount} errors.`;
  }
  if (job.totalGroups === 0) return "Parsing and validating rows…";
  return `Creating tasks… ${job.processedGroups} of ${job.totalGroups}`;
}

// ─── Shared errors modal ──────────────────────────────────────────────────────

function ErrorsModal({
  open, onClose, jobId, jobSerial, queryKey, fetchErrorsFn, downloadFn, filePrefix,
}: {
  open: boolean;
  onClose: () => void;
  jobId: number;
  jobSerial: number;
  queryKey: string;
  fetchErrorsFn: (q: any) => any;
  downloadFn: (id: number) => any;
  filePrefix: string;
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, isLoading } = useQuery([queryKey, jobId], fetchErrorsFn, { enabled: open && !!jobId });
  const errors: any[] = data?.data || [];

  // Group individual errors by their Excel row number
  const grouped = errors.reduce((acc: Record<number, any[]>, err: any) => {
    if (!acc[err.rowNumber]) acc[err.rowNumber] = [];
    acc[err.rowNumber].push(err);
    return acc;
  }, {});
  const groupedRows: { rowNumber: number; errors: any[] }[] = Object.keys(grouped)
    .map(k => ({ rowNumber: parseInt(k), errors: grouped[parseInt(k)] }))
    .sort((a, b) => a.rowNumber - b.rowNumber);

  const paginatedGroups = groupedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const res = await downloadFn(jobId);
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filePrefix}-errors-job-${jobId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      snack.error("Failed to download errors file.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: "80vh", display: "flex", flexDirection: "column" } }}>
      <DialogTitle sx={{ bgcolor: PRIMARY, color: "#fff", fontFamily: "muli_bold", fontSize: 15, display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, px: 3 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorOutlineIcon sx={{ fontSize: 18, color: SECONDARY }} />
          Row Errors — Import #{jobSerial}
          {!isLoading && (
            <Box display="flex" gap={0.8} ml={1}>
              <Chip label={`${errors.length} error${errors.length !== 1 ? "s" : ""}`} size="small" sx={{ bgcolor: SECONDARY, color: "#fff", fontFamily: "muli_bold", fontSize: 11 }} />
              {groupedRows.length !== errors.length && (
                <Chip label={`${groupedRows.length} row${groupedRows.length !== 1 ? "s" : ""}`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontFamily: "muli_bold", fontSize: 11 }} />
              )}
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 15 }} />} onClick={handleDownload}
            disabled={isDownloading || isLoading || errors.length === 0}
            sx={{ fontFamily: "muli_medium", fontSize: 12, textTransform: "none", color: "#fff", borderColor: "rgba(255,255,255,0.4)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" }, "&.Mui-disabled": { color: "rgba(255,255,255,0.3)", borderColor: "rgba(255,255,255,0.15)" } }}>
            {isDownloading ? "Downloading…" : "Download"}
          </Button>
          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)" }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}><Typography variant="body2" color="text.secondary">Loading errors…</Typography></Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Row #", "Column", "Error Message"].map((h) => (
                      <TableCell key={h} sx={{ fontFamily: "muli_bold", fontSize: 12, bgcolor: "rgba(24,47,83,0.05)", color: PRIMARY, borderBottom: `2px solid rgba(24,47,83,0.12)` }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedGroups.map((group, groupIdx) =>
                    group.errors.map((err: any, errIdx: number) => (
                      <TableRow key={`${group.rowNumber}-${errIdx}`}
                        sx={{ bgcolor: groupIdx % 2 === 0 ? "rgba(24,47,83,0.025)" : undefined, "&:hover td": { bgcolor: "rgba(24,47,83,0.05)" } }}>
                        {errIdx === 0 && (
                          <TableCell rowSpan={group.errors.length}
                            sx={{ fontFamily: "muli_bold", fontSize: 13, color: PRIMARY, width: 90, verticalAlign: "top", pt: 1.5,
                              borderBottom: `1px solid rgba(24,47,83,0.08)` }}>
                            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.6}>
                              <span>{group.rowNumber}</span>
                              {group.errors.length > 1 && (
                                <Chip label={`${group.errors.length}×`} size="small"
                                  sx={{ height: 16, fontSize: 10, fontFamily: "muli_bold", bgcolor: `${SECONDARY}18`, color: SECONDARY, "& .MuiChip-label": { px: 0.8 } }} />
                              )}
                            </Box>
                          </TableCell>
                        )}
                        <TableCell sx={{ fontFamily: "muli_medium", fontSize: 12, width: 170, color: "text.secondary", borderBottom: `1px solid rgba(24,47,83,0.06)` }}>
                          {err.columnName || "—"}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "muli_regular", fontSize: 13, borderBottom: `1px solid rgba(24,47,83,0.06)` }}>
                          {err.errorMessage}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
            <TablePagination component="div" count={groupedRows.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25]} sx={{ borderTop: `1px solid rgba(24,47,83,0.08)`, flexShrink: 0 }} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Generic import panel ─────────────────────────────────────────────────────

interface PanelConfig {
  title: string;
  subtitle: string;
  localStorageKey: string;
  pollUrl: (id: number) => string;
  mutateFn: (data: FormData) => any;
  listJobsFn: () => any;
  listQueryKey: string;
  errorQueryKey: string;
  fetchErrorsFn: (q: any) => any;
  downloadErrorsFn: (id: number) => any;
  filePrefix: string;
  sampleFileUrl?: string;
  drawerTitle: string;
  cardHeaderColor: string;
  cardTextColor: string;
}

function ImportPanel({ config, queryClient, onProcessingChange, blockedByOther }: {
  config: PanelConfig;
  queryClient: any;
  onProcessingChange: (v: boolean) => void;
  blockedByOther: boolean;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [errorsModalOpen, setErrorsModalOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(5);
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<number | null>(() => {
    const stored = localStorage.getItem(config.localStorageKey);
    return stored ? parseInt(stored, 10) : null;
  });

  const handleComplete = useCallback(() => {
    queryClient.invalidateQueries(config.listQueryKey);
  }, [queryClient, config.listQueryKey]);

  const { job, isPolling, setJob } = useJobPolling(jobId, config.pollUrl, handleComplete);

  const { data: jobsData, isLoading: isJobsLoading, refetch: refetchJobs } = useQuery(
    [config.listQueryKey], config.listJobsFn
  );
  const allJobs: any[] = jobsData?.data || [];

  useEffect(() => {
    if (jobId === null && allJobs.length > 0) setJobId(allJobs[0].id);
  }, [allJobs]);

  const { mutate, isLoading } = useMutation(config.mutateFn, {
    onSuccess: (res: any) => {
      const newJobId = res?.data?.jobId;
      localStorage.setItem(config.localStorageKey, String(newJobId));
      setJob(null);
      setJobId(newJobId);
      setDrawerOpen(false);
      setFile(null);
      snack.success("Import started. Tracking progress below…");
      refetchJobs();
    },
    onError: (err: any) => snack.error(handleError(err)),
  });

  const handleSubmit = () => {
    if (!file) return snack.error("Please select a file");
    const valid = file.type === FILETYPES.SHEET1 || file.type === FILETYPES.SHEET2 || file.type === FILETYPES.SHEET3;
    if (!valid) return snack.error("Invalid file type. Only .xls or .xlsx files are accepted");
    const formData = new FormData();
    formData.append("file", file);
    mutate(formData);
  };

  const isProcessing = job?.status === "processing";
  const isCompleted = job?.status === "completed";
  const isFailed = job?.status === "failed";
  const isDone = isCompleted || isFailed;

  const progress = isDone
    ? (isCompleted && (job.successCount ?? 0) > 0 ? 100 : 0)
    : job?.totalGroups > 0
    ? Math.round((job.processedGroups / job.totalGroups) * 100)
    : 0;

  const showFailedBar = isFailed || (isCompleted && (job?.successCount ?? 0) === 0);
  const showIndeterminate = isProcessing && job?.totalGroups === 0;
  const hasErrors = job?.errorCount > 0 && isDone;

  const jobIdx = job ? allJobs.findIndex(j => j.id === job.id) : -1;
  const jobSerial = jobIdx >= 0 ? allJobs.length - jobIdx : 1;

  useEffect(() => { onProcessingChange(isProcessing); }, [isProcessing]);

  const paginatedJobs = allJobs.slice(historyPage * historyRowsPerPage, historyPage * historyRowsPerPage + historyRowsPerPage);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "auto" }}>

      {/* Panel header */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: `1px solid rgba(24,47,83,0.08)`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ fontFamily: "muli_bold", fontSize: 14, color: PRIMARY }}>{config.title}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3 }}>{config.subtitle}</Typography>
        </Box>
        <Button variant="contained" color="secondary" size="small" startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
          onClick={() => setDrawerOpen(true)} disabled={isProcessing || isJobsLoading || blockedByOther}
          sx={{ whiteSpace: "nowrap", ml: 1, fontSize: 12 }}>
          Import
        </Button>
      </Box>

      {/* Panel body */}
      <Box sx={{ flex: 1, p: 2 }}>

        {/* Active job card */}
        {job && (
          <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, overflow: "hidden", border: `1px solid ${config.cardHeaderColor}` }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: config.cardHeaderColor, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box display="flex" alignItems="center" gap={1}>
                <InsertDriveFileOutlinedIcon sx={{ color: config.cardTextColor, opacity: 0.6, fontSize: 18 }} />
                <Box>
                  <Typography sx={{ fontFamily: "muli_bold", fontSize: 13, color: config.cardTextColor }}>Import #{jobSerial}</Typography>
                  <Typography sx={{ fontFamily: "muli_regular", fontSize: 11, color: config.cardTextColor, opacity: 0.65 }}>{job.fileName}</Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                {isPolling && (
                  <Box display="flex" alignItems="center" gap={0.6}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#4caf50", animation: "blink 1.2s infinite", "@keyframes blink": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.25 } } }} />
                    <Typography sx={{ fontSize: 10, color: config.cardTextColor, opacity: 0.65, fontFamily: "muli_regular" }}>Live</Typography>
                  </Box>
                )}
                <Chip label={job.status.toUpperCase()} color={statusColor(job.status)} size="small" sx={{ fontFamily: "muli_bold", fontSize: 10 }} />
              </Box>
            </Box>

            <Box px={2} py={2}>
              <GradientProgressBar value={progress} indeterminate={showIndeterminate} failed={showFailedBar} />
              <Typography variant="caption" sx={{ display: "block", mt: 0.6, mb: 2, color: showFailedBar ? SECONDARY : "text.secondary" }}>
                {getPhaseText(job)}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <StatCard label="Rows" value={job.totalRows} color={PRIMARY} bgcolor="rgba(24,47,83,0.05)" />
                <StatCard label="Valid" value={job.totalGroups} color="#1565c0" bgcolor="rgba(21,101,192,0.06)" />
                <StatCard label="Done" value={job.processedGroups} color="#e65100" bgcolor="rgba(230,81,0,0.06)" />
                <StatCard label="Success" value={job.successCount} color="#2e7d32" bgcolor="rgba(46,125,50,0.06)" />
                <StatCard label="Errors" value={job.errorCount} color={SECONDARY} bgcolor="rgba(228,70,82,0.06)" />
              </Box>
              <Box display="flex" gap={2} mt={1.5} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  Started: <span style={{ color: PRIMARY, fontFamily: "muli_medium" }}>{moment(job.createdAt).format("DD MMM YYYY, hh:mm A")}</span>
                </Typography>
                {job.completedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Completed: <span style={{ color: "#2e7d32", fontFamily: "muli_medium" }}>{moment(job.completedAt).format("DD MMM YY, hh:mm A")}</span>
                  </Typography>
                )}
              </Box>
            </Box>

            {(hasErrors || (job.status === "completed" && job.errorCount === 0 && job.successCount > 0)) && (
              <Box px={2} py={1.2} sx={{ borderTop: `1px solid rgba(24,47,83,0.08)`, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: hasErrors ? "rgba(228,70,82,0.04)" : "rgba(46,125,50,0.04)" }}>
                {hasErrors ? (
                  <>
                    <Box display="flex" alignItems="center" gap={0.8}>
                      <ErrorOutlineIcon sx={{ fontSize: 15, color: SECONDARY }} />
                      <Typography sx={{ fontFamily: "muli_medium", fontSize: 12, color: SECONDARY }}>
                        {job.errorCount} error{job.errorCount !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" color="error" startIcon={<OpenInFullIcon sx={{ fontSize: 13 }} />}
                      onClick={() => setErrorsModalOpen(true)}
                      sx={{ fontFamily: "muli_medium", fontSize: 11, textTransform: "none", py: 0.4 }}>
                      View
                    </Button>
                  </>
                ) : (
                  <Box display="flex" alignItems="center" gap={0.8}>
                    <CheckCircleOutlineIcon sx={{ color: "#2e7d32", fontSize: 15 }} />
                    <Typography sx={{ fontFamily: "muli_medium", fontSize: 12, color: "#2e7d32" }}>
                      {job.successCount} task{job.successCount !== 1 ? "s" : ""} created successfully.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        )}

        {/* History toggle */}
        <Button variant="outlined" size="small"
          startIcon={showHistory ? <KeyboardArrowUpIcon /> : <HistoryIcon />}
          onClick={() => { setShowHistory((p) => !p); if (!showHistory) refetchJobs(); }}
          sx={{ fontFamily: "muli_medium", fontSize: 12, textTransform: "none", color: PRIMARY, borderColor: "rgba(24,47,83,0.25)", "&:hover": { borderColor: PRIMARY, bgcolor: "rgba(24,47,83,0.04)" }, mb: showHistory ? 1.5 : 0 }}>
          {showHistory ? "Hide History" : "Show History"}
        </Button>

        {/* History table */}
        {showHistory && (
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: `1px solid rgba(24,47,83,0.1)`, mt: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Status", "Rows", "Success", "Errors", "Started"].map((h) => (
                    <TableCell key={h} sx={{ fontFamily: "muli_bold", fontSize: 11, color: PRIMARY, bgcolor: "rgba(24,47,83,0.03)", borderBottom: `2px solid rgba(24,47,83,0.1)`, py: 1 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedJobs.map((j: any, idx: number) => (
                  <TableRow key={j.id} hover onClick={() => { setJobId(j.id); setShowHistory(false); }}
                    sx={{ cursor: "pointer", bgcolor: jobId === j.id ? "rgba(24,47,83,0.05)" : undefined, "&:hover": { bgcolor: "rgba(24,47,83,0.04)" } }}>
                    <TableCell sx={{ fontFamily: "muli_medium", fontSize: 12 }}>#{allJobs.length - (historyPage * historyRowsPerPage + idx)}</TableCell>
                    <TableCell><Chip label={j.status.toUpperCase()} color={statusColor(j.status)} size="small" sx={{ fontFamily: "muli_bold", fontSize: 10 }} /></TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{j.totalRows ?? 0}</TableCell>
                    <TableCell sx={{ fontFamily: "muli_bold", fontSize: 12, color: "#2e7d32" }}>{j.successCount ?? 0}</TableCell>
                    <TableCell sx={{ fontFamily: "muli_bold", fontSize: 12, color: j.errorCount > 0 ? SECONDARY : "text.secondary" }}>{j.errorCount ?? 0}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>{moment(j.createdAt).format("DD MMM YY, hh:mm A")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination component="div" count={allJobs.length} page={historyPage}
              onPageChange={(_, p) => setHistoryPage(p)} rowsPerPage={historyRowsPerPage}
              onRowsPerPageChange={(e) => { setHistoryRowsPerPage(parseInt(e.target.value, 10)); setHistoryPage(0); }}
              rowsPerPageOptions={[5, 10, 20]} sx={{ borderTop: `1px solid rgba(24,47,83,0.08)` }} />
          </Paper>
        )}
      </Box>

      {/* Upload drawer */}
      <DrawerWrapper open={drawerOpen} setOpen={setDrawerOpen} title={config.drawerTitle}>
        <FileDrop onChange={(files: File[]) => setFile(files[0])} />
        <Box display="flex" justifyContent="space-between" mt={1} mb={1}>
          <Typography variant="caption" color="rgba(0,0,0,0.5)">*Only .xls or .xlsx files are accepted</Typography>
          {config.sampleFileUrl && (
            <a href={config.sampleFileUrl} style={{ textDecoration: "none" }}>
              <Typography variant="caption" color="secondary">Download Sample</Typography>
            </a>
          )}
        </Box>
        <LoadingButton onClick={handleSubmit} variant="contained" loading={isLoading} disabled={isLoading} sx={{ mt: 1 }} fullWidth color="secondary" title="Start Import" />
      </DrawerWrapper>

      {/* Errors modal */}
      {hasErrors && (
        <ErrorsModal
          open={errorsModalOpen}
          onClose={() => setErrorsModalOpen(false)}
          jobId={job.id}
          jobSerial={jobSerial}
          queryKey={config.errorQueryKey}
          fetchErrorsFn={config.fetchErrorsFn}
          downloadFn={config.downloadErrorsFn}
          filePrefix={config.filePrefix}
        />
      )}
    </Box>
  );
}

// ─── Combined page ────────────────────────────────────────────────────────────

const NON_RECURRING_CONFIG: PanelConfig = {
  title: "Non-Recurring Tasks",
  subtitle: "Upload Excel to create non-recurring tasks in bulk.",
  localStorageKey: "non_recurring_bulk_import_job_id",
  pollUrl: (id) => `/tasks/bulk-import/${id}`,
  mutateFn: bulkImportNonRecurringTasks,
  listJobsFn: getNonRecurringBulkImportJobs,
  listQueryKey: "non-recurring-bulk-import-jobs",
  errorQueryKey: "non-recurring-job-errors",
  fetchErrorsFn: getNonRecurringJobErrors,
  downloadErrorsFn: downloadNonRecurringJobErrors,
  filePrefix: "non-recurring-import",
  sampleFileUrl: "https://vider-prod-bucket.s3.ap-south-1.amazonaws.com/Bulk_NonRecurringTaskImportUpdated.xlsx",
  drawerTitle: "Import Non-Recurring Tasks",
  cardHeaderColor: "#dbeafe",
  cardTextColor: "#1e3a8a",
};

const RECURRING_CONFIG: PanelConfig = {
  title: "Recurring Tasks",
  subtitle: "Upload Excel to create recurring task profiles in bulk.",
  localStorageKey: "recurring_bulk_import_job_id",
  pollUrl: (id) => `/recurring-profile/bulk-import/${id}`,
  mutateFn: bulkImportRecurringTasks,
  listJobsFn: getRecurringBulkImportJobs,
  listQueryKey: "recurring-bulk-import-jobs",
  errorQueryKey: "recurring-job-errors",
  fetchErrorsFn: getRecurringJobErrors,
  downloadErrorsFn: downloadRecurringJobErrors,
  filePrefix: "recurring-import",
  sampleFileUrl: "https://vider-prod-bucket.s3.ap-south-1.amazonaws.com/Bulk_RecurringTaskImportUpdated.xlsx",
  drawerTitle: "Import Recurring Tasks",
  cardHeaderColor: "#dcfce7",
  cardTextColor: "#14532d",
};

function TasksBulkImport() {
  useTitle("Tasks | Bulk Import");
  const queryClient = useQueryClient();
  const [nonRecurringProcessing, setNonRecurringProcessing] = useState(false);
  const [recurringProcessing, setRecurringProcessing] = useState(false);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Two-column layout */}
      <Box sx={{ flex: 1, overflow: "hidden", display: "flex", gap: 0 }}>

        {/* Non-Recurring panel */}
        <Box sx={{ flex: 1, borderRight: `2px solid rgba(24,47,83,0.08)`, overflow: "auto" }}>
          <ImportPanel config={NON_RECURRING_CONFIG} queryClient={queryClient}
            onProcessingChange={setNonRecurringProcessing} blockedByOther={recurringProcessing} />
        </Box>

        {/* Recurring panel */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <ImportPanel config={RECURRING_CONFIG} queryClient={queryClient}
            onProcessingChange={setRecurringProcessing} blockedByOther={nonRecurringProcessing} />
        </Box>
      </Box>
    </Box>
  );
}

export default TasksBulkImport;
